import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro `hca.obchodniPripady` tRPC router.
 * Pipeline + obchodní případy + provize (plán §A, §D).
 */

export const opFazeEnum = z.enum([
  "OSLOVENI",
  "SCHUZKA",
  "NABIDKA",
  "UZAVRENO",
  "FAKTUROVANO",
  "ZTRACENO",
]);
export type OpFaze = z.infer<typeof opFazeEnum>;

/** Fáze, na které obchodník nesmí skočit z libovolné jiné (forward-only). */
export const OP_STAGE_ORDER: readonly OpFaze[] = [
  "OSLOVENI",
  "SCHUZKA",
  "NABIDKA",
  "UZAVRENO",
  "FAKTUROVANO",
] as const;

const uuidMsg = "Neplatné UUID";

/** Vytvoření obchodního případu. `obchodnikId` se doplní v routeru z ctx.user.id. */
export const createOpSchema = z.object({
  klientId: z.string().uuid(uuidMsg),
  productId: optionalUuid(uuidMsg),
  castkaPredpokladana: z.coerce
    .number()
    .nonnegative("Částka nesmí být záporná")
    .max(10_000_000, "Částka je mimo povolený rozsah")
    .optional(),
  provizePct: z.coerce
    .number()
    .min(0, "Provize nesmí být záporná")
    .max(100, "Provize musí být 100 % nebo méně")
    .optional(),
  poznamka: z.string().max(5000, "Poznámka musí být 5000 znaků nebo méně").optional(),
});

/**
 * Partial update — nikdy nemění `faze` (na to je moveStage).
 * `null` = smazat hodnotu (odebrat produkt/částku), `undefined` = beze změny.
 */
export const updateOpSchema = createOpSchema.partial().extend({
  id: z.string().uuid(uuidMsg),
  productId: z.string().uuid(uuidMsg).nullable().optional(),
  castkaPredpokladana: z.coerce
    .number()
    .nonnegative("Částka nesmí být záporná")
    .max(10_000_000, "Částka je mimo povolený rozsah")
    .nullable()
    .optional(),
  poznamka: z.string().max(5000, "Poznámka musí být 5000 znaků nebo méně").nullable().optional(),
});

/**
 * Posun fáze. Když cílová fáze je ZTRACENO, `ztracenoDuvod` je povinný —
 * drží data integrity pro analytics „proč jsme prohráli".
 */
export const moveStageSchema = z
  .object({
    id: z.string().uuid(uuidMsg),
    faze: opFazeEnum,
    ztracenoDuvod: z.string().max(500, "Důvod musí být 500 znaků nebo méně").optional(),
  })
  .refine(
    (data) =>
      data.faze !== "ZTRACENO" || (data.ztracenoDuvod && data.ztracenoDuvod.trim().length > 0),
    { message: "Při fázi ZTRACENO je povinný důvod.", path: ["ztracenoDuvod"] },
  );

/** Seznam OP s filtry. `obchodnikId` filter smí použít jen admin / hca.op.read_all. */
export const listOpSchema = paginationSchema.extend({
  faze: opFazeEnum.optional(),
  obchodnikId: z.string().max(255, "ID musí být 255 znaků nebo méně").optional(),
  klientId: optionalUuid(uuidMsg),
});

/** ID-only schema. */
export const opIdSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Provize report — filtr období. */
export const listProvizeSchema = z.object({
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  obchodnikId: z.string().max(255, "ID musí být 255 znaků nebo méně").optional(),
});

export type CreateOpInput = z.infer<typeof createOpSchema>;
export type UpdateOpInput = z.infer<typeof updateOpSchema>;
export type MoveStageInput = z.infer<typeof moveStageSchema>;
export type ListOpInput = z.infer<typeof listOpSchema>;
export type OpIdInput = z.infer<typeof opIdSchema>;
export type ListProvizeInput = z.infer<typeof listProvizeSchema>;
